import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { ReactKeycloakProvider as KeycloakProvider } from "@react-keycloak/web";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type EvaluationContext, InMemoryProvider, OpenFeature, OpenFeatureProvider } from "@openfeature/react-sdk";

import keycloak, { getInitOptions } from "src/services/keycloak";
import { ProfileProvider } from "src/providers/profile-provider";
import { TokenProvider } from "src/providers/token-provider";
import { AnalyticsProvider } from "src/providers/analytics-provider";
import TipsProvider from "./providers/tip-provider";

import { Routes } from "./Routes";
import { isStaging } from "./constants";

interface LocationState {
  from: {
    pathname: string;
    search: string;
    hash: string;
  };
}

/**
 * "on" for staging, development, and test environments, "off" for production
 */
const onStagingOrDevOrTest = (context: EvaluationContext) => {
  if (context.isStaging) {
    return "on";
  }

  if (context.isDevelopment || context.isTest) {
    return "on";
  }

  return "off";
};

const allOn = (_: EvaluationContext) => {
  return "on";
};

const flagConfig = {
  "scim-token-ui": {
    disabled: false,
    variants: {
      on: true,
      off: false,
    },
    defaultVariant: "off",
    contextEvaluator: allOn,
  },
  "auto-join-ui": {
    disabled: false,
    variants: {
      on: true,
      off: false,
    },
    defaultVariant: "off",
    contextEvaluator: allOn,
  },
};

OpenFeature.setProvider(new InMemoryProvider(flagConfig));
const openFeatureClient = OpenFeature.getClient();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // ✅ turns retries off in tests to avoid timeout/flakiness
      retry: process.env.NODE_ENV !== "test",
    },
  },
  logger: {
    // eslint-disable-next-line no-console
    log: console.log,
    // eslint-disable-next-line no-console
    warn: console.warn,
    // eslint-disable-next-line no-console
    error: process.env.NODE_ENV === "test" ? () => {} : console.error,
  },
});

const App = () => {
  const location = useLocation<LocationState | undefined>();
  const responseMode = queryString.parse(location.search).response_mode?.toString();
  const initOptions = useMemo(() => getInitOptions(location.pathname, responseMode), [location.pathname, responseMode]);

  useEffect(() => {
    OpenFeature.setContext({
      isStaging: isStaging === true,
      isDevelopment: process.env.NODE_ENV === "development",
      isTest: process.env.NODE_ENV === "test",
    }).catch((error) => {
      // eslint-disable-next-line no-console
      console.error("Error setting OpenFeature context", error);
    });
  }, []);

  return (
    <OpenFeatureProvider client={openFeatureClient}>
      <KeycloakProvider authClient={keycloak} initOptions={initOptions}>
        <TokenProvider>
          <ProfileProvider>
            <AnalyticsProvider>
              <QueryClientProvider client={queryClient}>
                <TipsProvider>
                  <Routes />
                </TipsProvider>
              </QueryClientProvider>
            </AnalyticsProvider>
          </ProfileProvider>
        </TokenProvider>
      </KeycloakProvider>
    </OpenFeatureProvider>
  );
};

export type { LocationState };
export default App;
